import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../app/providers/LanguageProvider';
import { useTheme } from '../../app/providers/ThemeProvider';

/**
 * Native `useReducedMotion` — wraps matchMedia('(prefers-reduced-motion:
 * reduce)'). Replaces the motion/react import so this hooks barrel doesn't
 * drag the motion library (~50 KB gzipped) into anything that imports from it
 * (e.g. PageTransition in main). Returns true when the OS reports reduced
 * motion. Tracks live changes via 'change' events.
 */
export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
};

/**
 * Returns the active document direction ('ltr' | 'rtl') from LanguageProvider.
 */
export const useDirection = () => {
  const { direction } = useLanguage();
  return direction;
};

/**
 * Returns true when the resolved theme is dark. Falls back to a media-query
 * listener if ThemeProvider isn't mounted (defensive — should never happen
 * in app shell, but matters for isolated component tests).
 */
export const usePrefersDarkMode = () => {
  const themeCtx = useTheme();
  const resolved = themeCtx?.resolvedTheme;

  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (resolved) return undefined; // Provider is authoritative
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [resolved]);

  if (resolved) return resolved === 'dark';
  return systemDark;
};

/**
 * Memoized motion environment for variant factories.
 * Pass the result as the first argument to any variant factory in
 * `variants.js`.
 */
export const useMotionEnv = () => {
  const reduced = useReducedMotion();
  const direction = useDirection();
  return useMemo(() => ({ reduced: !!reduced, direction }), [reduced, direction]);
};

/**
 * Reveal-on-scroll hook. Returns [ref, inView]. Uses IntersectionObserver and
 * disconnects after the first reveal when `once === true`.
 */
export const useScrollReveal = ({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
} = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: assume in view (better than never animating).
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
};

/**
 * Default viewport options for `whileInView` usage. Re-exported from the
 * compatibility shim in `pages/PublicPages/motionPresets.js`.
 *
 * Margin matches the legacy preset (`'-10% 0px -10% 0px'`) so existing call
 * sites in HomePage / About / Feature / Guides keep their exact reveal
 * thresholds.
 */
export const REVEAL_VIEWPORT = { once: true, margin: '-10% 0px -10% 0px' };
